import { Package, Sparkles, BadgeCheck, Square, Award, Star, Globe, Crown, Trophy, Shield } from "lucide-react";
import { Flag } from "./Flag";

const ICONS = { Sparkles, BadgeCheck, Square, Award, Star, Globe, Crown, Trophy, Shield, Package };
const RARITY = {
  legendary: { label: "Legendary", ring: "ring-[#E5B842]/70", text: "text-[#E5B842]" },
  epic: { label: "Epic", ring: "ring-[#C99E30]/60", text: "text-[#d9b24e]" },
  rare: { label: "Rare", ring: "ring-[#5b74a6]/60", text: "text-[#9fb4d9]" },
  common: { label: "Common", ring: "ring-[#3a4150]/70", text: "text-slate-300" },
};
const FLAGS = { GRC: "gr", CYP: "cy" };

function CardTile({ row }) {
  const it = row.item; const r = RARITY[it.rarity] || RARITY.common;
  const Icon = ICONS[it.icon] || Trophy;
  const accent = it.accent || "#E5B842";
  return (
    <div data-testid={`collection-card-${it.sku}`} className={`relative rounded-xl bg-[#0d0d0d] border border-[#1e1e1e] p-3 ring-1 ${r.ring}`}>
      {row.qty > 1 && <span className="absolute top-2 right-2 z-10 text-[10px] font-heading font-black text-black bg-[#E5B842] rounded-full px-1.5 py-0.5">×{row.qty}</span>}
      <div className="relative h-24 rounded-lg grid place-items-center overflow-hidden" style={{ background: `radial-gradient(120% 120% at 50% 0%, ${accent}22 0%, #070707 60%)` }}>
        <div className="absolute inset-0 mesh-grid opacity-[0.18]" />
        {it.flag && FLAGS[it.flag] && <span className="absolute opacity-20"><Flag country={{ iso2: FLAGS[it.flag] }} width={52} /></span>}
        <Icon className="w-8 h-8 relative" style={{ color: accent }} strokeWidth={1.5} />
      </div>
      <div className={`mt-2 text-[9px] font-mono uppercase tracking-[0.16em] ${r.text}`}>{r.label}</div>
      <div className="font-heading text-xs font-bold uppercase tracking-tight text-slate-50 leading-tight break-words">{it.name}</div>
    </div>
  );
}

export function CollectionShowcase({ collection }) {
  if (!collection) return null;
  const { cards = [], distinct = 0, equipped = {} } = collection;

  return (
    <div data-testid="passport-collection" className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#141414] to-[#070707] p-6">
      <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#E5B842]" />
            <h2 className="font-heading text-xl font-extrabold uppercase tracking-tight text-slate-50">Collection</h2>
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{distinct} cards</span>
          </div>
          {(equipped.badge || equipped.frame) && (
            <div className="flex items-center gap-2" data-testid="collection-equipped">
              {equipped.frame && <span className="text-[10px] font-mono uppercase tracking-wider text-[#E5B842] border border-[#E5B842]/40 rounded-full px-2 py-0.5">Frame: {equipped.frame.name}</span>}
              {equipped.badge && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#E5B842] border border-[#E5B842]/40 rounded-full px-2 py-0.5"><BadgeCheck className="w-3 h-3" /> {equipped.badge.name}</span>}
            </div>
          )}
        </div>

        {cards.length === 0 ? (
          <div data-testid="collection-empty" className="rounded-xl border border-dashed border-[#2b2512] p-8 text-center">
            <Package className="w-7 h-7 text-slate-700 mx-auto mb-2" />
            <div className="text-sm text-slate-500">No cards collected yet.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {cards.map((row) => <CardTile key={row.sku} row={row} />)}
          </div>
        )}
      </div>
    </div>
  );
}
